import { ArrowUpCircleIcon, PencilSquareIcon } from "@heroicons/react/16/solid";
import { ChatMessageDTO } from "@tumtutor/shared/dto";
import { RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface ChatInputBarProps {
  handleSubmit: (input: string) => void;
  isStreaming: boolean;
  isFetchingChatHistory: boolean;
  textareaRef: RefObject<HTMLTextAreaElement>;
  newChatPressed: () => void;
  messages: ChatMessageDTO[];
}

export function ChatInputBar({
  handleSubmit,
  isStreaming,
  isFetchingChatHistory,
  textareaRef,
  newChatPressed,
  messages,
}: ChatInputBarProps) {
  const [input, setInput] = useState<string>("");
  const { t } = useTranslation(["chatPage"]);

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    handleSubmit(input);
    setInput("");
  }

  useEffect(() => {
    textareaRef.current?.focus();
  }, [textareaRef.current]);

  return (
    <form
      onSubmit={handleFormSubmit}
      className="flex items-center rounded-2xl bg-neutral"
    >
      <textarea
        ref={textareaRef}
        value={input}
        required
        maxLength={5000}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (
            e.key === "Enter" &&
            !e.shiftKey &&
            !isStreaming &&
            !isFetchingChatHistory &&
            input.trim() !== ""
          ) {
            handleFormSubmit(e);
          }
        }}
        onInput={(e) => {
          e.currentTarget.style.height = "auto";
          e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
        }}
        className="mb-2 max-h-48 w-full resize-none overflow-y-auto bg-transparent px-lg pt-sm focus:outline-none sm:pb-0"
        placeholder={t("inputPlaceholderChat")}
      />

      <div className="mr-2 flex h-full flex-row items-end gap-2">
        <button
          type="submit"
          className="mb-1"
          disabled={isStreaming || isFetchingChatHistory}
        >
          <ArrowUpCircleIcon className="h-8 w-8 sm:h-9 sm:w-9" />
        </button>
        <div data-tip={t("tooltipNewChat")} className="tooltip hidden sm:block">
          {" "}
          <button
            tabIndex={0}
            disabled={isStreaming || messages.length === 0}
            onClick={() => {
              newChatPressed();
              textareaRef.current?.focus();
            }}
            className="mb-1"
            type="button"
          >
            <PencilSquareIcon className="h-8 w-8" />
          </button>
        </div>
      </div>
    </form>
  );
}
